import { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import { Pencil, Check, X, MapPin, Compass, Link as LinkIcon } from "lucide-react";
import { Link as RouterLink } from "react-router-dom";
import { ConnectButton } from "@/components/ConnectButton";
import api from "@/lib/api";
import { Avatar, LevelBadge, ProBadge } from "@/components/common";
import { PostCard } from "@/pages/Feed";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

function TagSection({ label, tags, muted }) {
  if (!tags || tags.length === 0) return null;
  return (
    <div>
      <div className="text-xs font-medium text-muted-foreground mb-2">{label}</div>
      <div className="flex flex-wrap gap-1.5">
        {tags.map((t) => (
          <span key={t} className={`text-xs rounded-full px-2.5 py-1 ${muted ? "border border-border text-foreground" : "bg-secondary text-cobalt"}`}>{t}</span>
        ))}
      </div>
    </div>
  );
}

export default function Profile() {
  const { userId } = useParams();
  const { user, setUser } = useAuth();
  const [profile, setProfile] = useState(null);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ name: "", title: "", bio: "" });

  const load = useCallback(async () => {
    const res = await api.get(`/members/${userId}`);
    setProfile(res.data);
    setForm({ name: res.data.name, title: res.data.title || "", bio: res.data.bio || "" });
  }, [userId]);

  useEffect(() => { load(); }, [load]);

  const isMe = user && profile && user.user_id === profile.user_id;

  const save = async () => {
    try {
      const res = await api.put("/profile", form);
      setUser(res.data);
      setEditing(false);
      load();
      toast.success("Profile updated");
    } catch (e) {
      toast.error("Could not save");
    }
  };

  if (!profile) return <div className="text-center py-16 text-muted-foreground eyebrow">Loading…</div>;

  return (
    <div className="space-y-8">
      <div className="border border-border bg-card rounded-xl p-6 sm:p-8" data-testid="profile-hero">
        <div className="flex flex-col sm:flex-row items-start gap-6">
          <Avatar src={profile.picture} name={profile.name} size={110} className="border-2" />
          <div className="flex-1 min-w-0 w-full">
            {editing ? (
              <div className="space-y-3">
                <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                  data-testid="profile-name-input"
                  className="w-full bg-transparent border-b border-border py-2 font-serif text-2xl outline-none" placeholder="Name" />
                <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
                  data-testid="profile-title-input"
                  className="w-full bg-transparent border-b border-border py-2 text-sm outline-none" placeholder="Title / role" />
                <textarea value={form.bio} onChange={(e) => setForm({ ...form, bio: e.target.value })}
                  data-testid="profile-bio-input" rows={3}
                  className="w-full bg-transparent border border-border p-3 text-sm outline-none resize-none" placeholder="Bio" />
                <div className="flex gap-2">
                  <button onClick={save} data-testid="profile-save-button"
                    className="inline-flex items-center gap-1.5 bg-gold text-accent-foreground rounded-md px-4 py-2 text-sm hover:bg-gold-hover transition-colors">
                    <Check size={14} /> Save
                  </button>
                  <button onClick={() => setEditing(false)}
                    className="inline-flex items-center gap-1.5 border border-border px-4 py-2 text-sm hover:bg-secondary/50 transition-colors">
                    <X size={14} /> Cancel
                  </button>
                </div>
              </div>
            ) : (
              <>
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <h1 className="font-serif font-light text-3xl sm:text-4xl tracking-tight flex items-center gap-2 flex-wrap">
                      {profile.name}
                      {profile.founding_member ? <ProBadge founding /> : profile.plan === "pro" && <ProBadge />}
                    </h1>
                    {profile.title && <p className="text-sm text-muted-foreground mt-1">{profile.title}</p>}
                  </div>
                  {isMe && (
                    <button onClick={() => setEditing(true)} data-testid="edit-profile-button"
                      className="inline-flex items-center gap-1.5 border border-border px-3 py-2 text-sm hover:bg-secondary/50 transition-colors shrink-0">
                      <Pencil size={13} /> Edit
                    </button>
                  )}
                  {!isMe && (() => {
                    const first = (profile.name || "").split(" ")[0];
                    const prompts = [];
                    if (profile.expertise?.length) prompts.push(`Hi ${first} — I'd love to learn more about ${profile.expertise.slice(0,2).join(" & ")}. Could I pick your brain sometime?`);
                    if (profile.company_stage) prompts.push(`Hi ${first} — we're both around the ${profile.company_stage} stage. Would be great to swap experiences.`);
                    prompts.push(`Hi ${first} — really impressed by your work. Would love to connect on Ikigora.`);
                    return (
                      <div className="shrink-0">
                        <ConnectButton userId={profile.user_id} name={profile.name} suggestedNote={prompts[0]} prompts={prompts.slice(0, 3)} />
                      </div>
                    );
                  })()}
                </div>
                <div className="mt-3 flex flex-wrap items-center gap-3">
                  <LevelBadge level={profile.level} />
                  <span className="font-mono text-sm text-muted-foreground">{profile.points.toLocaleString()} points</span>
                  {profile.is_admin && <span className="text-[10px] font-mono uppercase tracking-wider border border-border px-2 py-0.5">Admin</span>}
                </div>
                {profile.bio && <p className="mt-4 text-sm text-muted-foreground leading-relaxed max-w-2xl">{profile.bio}</p>}
              </>
            )}
          </div>
        </div>
      </div>

      {(profile.role?.length || profile.expertise?.length || profile.seeking?.length || profile.goals?.length ||
        profile.industry?.length || profile.location || (profile.links && Object.keys(profile.links).length)) ? (
        <div className="border border-border bg-card rounded-xl p-6 sm:p-7" data-testid="profile-professional">
          <div className="flex items-center justify-between mb-5">
            <div className="eyebrow text-cobalt">Professional</div>
            {isMe && (
              <RouterLink to="/onboarding" data-testid="edit-interests-button"
                className="inline-flex items-center gap-1.5 text-xs border border-border rounded-md px-3 py-1.5 hover:border-cobalt hover:text-cobalt transition-colors">
                <Pencil size={12} /> Edit interests
              </RouterLink>
            )}
          </div>
          <div className="flex flex-wrap gap-x-6 gap-y-2 text-sm text-muted-foreground mb-5">
            {profile.role?.length > 0 && <span><span className="text-foreground font-medium">{profile.role.join(", ")}</span>{profile.seniority ? ` · ${profile.seniority}` : ""}</span>}
            {profile.company_stage && <span>{profile.company_stage}</span>}
            {profile.industry?.length > 0 && <span>{profile.industry.join(", ")}</span>}
            {profile.location && <span className="inline-flex items-center gap-1"><MapPin size={13} /> {profile.location}</span>}
          </div>
          <div className="space-y-4">
            <TagSection label="Expert in" tags={profile.expertise} />
            <TagSection label="Wants to explore" tags={profile.seeking} />
            <TagSection label="Goals" tags={profile.goals} muted />
            <TagSection label="Open to" tags={profile.open_to} muted />
            <TagSection label="Stack" tags={profile.tools} muted />
          </div>
          {profile.links && Object.keys(profile.links).length > 0 && (
            <div className="mt-5 pt-5 border-t border-border flex flex-wrap gap-4">
              {Object.entries(profile.links).map(([k, v]) => (
                <a key={k} href={v.startsWith("http") ? v : `https://${v}`} target="_blank" rel="noreferrer"
                  data-testid={`profile-link-${k}`}
                  className="inline-flex items-center gap-1.5 text-sm text-cobalt hover:underline capitalize">
                  <LinkIcon size={13} /> {k}
                </a>
              ))}
            </div>
          )}
        </div>
      ) : isMe && (
        <div className="border border-dashed border-border rounded-xl p-6 text-center" data-testid="profile-complete-prompt">
          <Compass size={24} className="mx-auto text-cobalt mb-2" />
          <p className="text-sm text-muted-foreground mb-3">Add your profession and interests to get matched with the right people.</p>
          <RouterLink to="/onboarding" className="inline-flex items-center gap-2 bg-cobalt text-white rounded-md px-4 py-2 text-sm font-semibold hover:bg-gold-hover transition-colors">
            Complete your profile
          </RouterLink>
        </div>
      )}

      <div>
        <div className="eyebrow text-gold mb-4">Activity · {profile.posts.length} posts</div>
        <div className="space-y-5">
          {profile.posts.length === 0 ? (
            <div className="text-sm text-muted-foreground py-8 text-center border border-dashed border-border">No posts yet.</div>
          ) : profile.posts.map((p) => (
            <PostCard key={p.post_id} post={p} onChange={load}
              onDelete={() => load()} />
          ))}
        </div>
      </div>
    </div>
  );
}
